// packages/web-ui/src/components/welcome/QuickActions.tsx
//
// W2-S10 Task 2.2 (AC #2) — three quick-action cards rendered inside the
// `<EmptyWelcome>` host. Order is locked by the AC: **Modify Architecture**,
// **Ask a Question**, **Domain Analysis**. Each card calls `onAction(intent)`
// where the intent is the string key the chat panel uses to seed the new
// session's first prompt.
//
// Pure presentation — no zustand wiring. The host owns the dispatch.

import type { JSX } from 'react';

export type QuickActionIntent = 'modify' | 'ask' | 'analyse';

export interface QuickActionCard {
  intent: QuickActionIntent;
  title: string;
  caption: string;
  /** Lucide icon glyph name; rendered as a decorative <span> placeholder
   *  so this presentation file stays free of `lucide-react` runtime deps
   *  (a future polish pass can swap in actual `<Icon>` components). */
  iconLabel: string;
}

/** Locked order from the AC. Exported for tests. */
export const QUICK_ACTIONS: ReadonlyArray<QuickActionCard> = [
  {
    intent: 'modify',
    title: 'Modify Architecture',
    caption: 'Propose KB changes — parts, ports, interfaces, connections.',
    iconLabel: 'Wrench',
  },
  {
    intent: 'ask',
    title: 'Ask a Question',
    caption: 'Read-only query — explore the model without changes.',
    iconLabel: 'MessageCircleQuestion',
  },
  {
    intent: 'analyse',
    title: 'Domain Analysis',
    caption: 'Run a domain-skill agent over the current workspace.',
    iconLabel: 'Microscope',
  },
];

export interface QuickActionsProps {
  /** Click handler dispatched with the canonical intent label. */
  onAction: (intent: QuickActionIntent) => void;
  /** Optional test-id override. */
  testId?: string;
}

export function QuickActions({
  onAction,
  testId = 'welcome-quick-actions',
}: QuickActionsProps): JSX.Element {
  return (
    <ul
      role="list"
      data-testid={testId}
      aria-label="Quick actions"
      className="grid grid-cols-1 gap-3 md:grid-cols-3"
    >
      {QUICK_ACTIONS.map((card) => (
        <li key={card.intent}>
          <button
            type="button"
            data-testid={`${testId}-${card.intent}`}
            onClick={() => onAction(card.intent)}
            className="border-taupe-300 bg-cream-50 hover:border-audi-red focus-visible:ring-taupe-900 group flex h-full w-full flex-col items-start gap-2 rounded-none border p-5 text-left transition-colors focus:outline-none focus-visible:ring-2"
          >
            {/*
             * Icon glyph is paired with text — never colour-only (NFR-A11Y-3).
             * The `aria-hidden` prevents SR double-read; the title/caption are
             * the SR-relevant text.
             */}
            <span
              aria-hidden="true"
              data-testid={`${testId}-${card.intent}-icon`}
              className="text-audi-red rounded-none text-[10px] font-semibold uppercase tracking-[0.18em] group-hover:text-audi-red"
            >
              {card.iconLabel}
            </span>
            <span className="text-fg text-base font-extrabold tracking-[-0.01em]">{card.title}</span>
            <span className="text-taupe-700 text-xs leading-relaxed">{card.caption}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
